/**
 * useTimelineData Hook
 *
 * Custom hook for managing sales timeline data with lazy loading and optimistic updates.
 * Handles both future items (unconfirmed recurring/suggestions) and past items (confirmed sales).
 *
 * @module useTimelineData
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import {
  supabase,
  fetchTimelineFuture,
  fetchSalesLightweight,
  transformLightweightToTimelineItem,
  fetchSaleById,
  transformToTimelineItem,
  updateSaleStatus,
  recalculateSaleItemPrices,
  applySaleItemPriceUpdates,
  createSale,
  createSaleItems,
  updateSale,
  deleteSaleItemsBySaleId,
  findExistingSale
} from '../../supabase-client'
import { toLocalDateString, parseDateString } from '../../utils/dateUtils'
import { useDataCache } from '../../contexts/DataCacheContext'
import { confirmInstance, createRecurringOrder, generateInstances } from '../../services/recurringOrderService'
import { confirmSuggestion } from '../../services/suggestionService'

// Constants for loading strategy
const INITIAL_FUTURE_DAYS = 30
const INITIAL_PAST_DAYS = 90
const FUTURE_LOAD_CHUNK = 150
const PAST_LOAD_CHUNK = 90
const MAX_FUTURE_DAYS = 180 // 6 months — cap for unconfirmed recurring/suggestion pagination
const CONFIRMED_FUTURE_DAYS = 365 * 5 // Effectively no cap — load all confirmed future sales

/**
 * Custom hook for timeline data management
 *
 * @returns {Object} Timeline data and control functions
 * @property {Array} futureItems - Unconfirmed items (recurring instances and suggestions)
 * @property {Array} pastItems - Confirmed sales (with committed recurring orders)
 * @property {boolean} loadingFuture - Loading state for future items
 * @property {boolean} loadingPast - Loading state for past items
 * @property {boolean} hasMoreFuture - Whether more future items can be loaded
 * @property {boolean} hasMorePast - Whether more past items can be loaded
 * @property {Function} loadMoreFuture - Load next chunk of future items
 * @property {Function} loadMorePast - Load next chunk of past items
 * @property {Function} confirmItem - Confirm an unconfirmed item
 * @property {Function} updateQuantity - Update quantity for an item's product
 * @property {Set} confirmingIds - Set of item IDs currently being confirmed
 * @property {Function} searchPast - Filter past items by search term
 */
// --- Session cache helpers (stale-while-revalidate) ---
const SESSION_KEY = 'odin_timeline_cache'
const SESSION_TTL = 5 * 60 * 1000 // 5 minutes

function loadTimelineFromSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const { ts, future, past } = JSON.parse(raw)
    if (Date.now() - ts > SESSION_TTL) return null
    return { future, past }
  } catch {
    return null
  }
}

function saveTimelineToSession(future, past) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({
      ts: Date.now(),
      future,
      past
    }))
  } catch {
    // quota exceeded — ignore
  }
}

/**
 * Map a CreateSaleModal item payload into the timeline lineItem shape.
 * Handles three categories: growing materials, fruiting blocks, regular mushroom items.
 */
function mapOptimisticItem(i) {
  if (i.growing_material_id) {
    return {
      id: i._lineId ? `gm-${i._lineId}` : i.growing_material_id,
      productId: null,
      productTypeId: null,
      growingMaterialId: i.growing_material_id,
      growingMaterial: { id: i.growing_material_id, name: i.name, unit_label: i.unit_label },
      strain: i.name,
      productTypeName: i.unit_label || '',
      quantity: i.quantity,
      price: i.unit_price,
      subtotal: i.subtotal
    }
  }
  if (i.block_format_id) {
    return {
      id: i._lineId ? `bf-${i._lineId}` : i.block_format_id,
      productId: i.product_id,
      productTypeId: null,
      blockFormatId: i.block_format_id,
      blockFormat: { id: i.block_format_id, name: i.format_name, weight_kg: i.format_weight_kg },
      strain: i.strain || 'Unknown',
      productTypeName: `Block (${i.format_name || 'Unknown'})`,
      quantity: i.quantity,
      price: i.cost_per_block,
      subtotal: i.subtotal
    }
  }
  return {
    id: i.product_id,
    productId: i.product_id,
    productTypeId: i.product_type_id,
    strain: i.strain || i.productName || 'Unknown',
    productTypeName: i.productTypeName || 'Unknown',
    quantity: i.quantity,
    price: i.price,
    subtotal: i.subtotal
  }
}

/**
 * Enrich lightweight timeline items with strain/productTypeName from cache
 */
function enrichFromCache(items, snapshot) {
  if (!snapshot?.products || !snapshot?.productTypes) return items
  const productMap = new Map(snapshot.products.map(p => [p.id, p]))
  const typeMap = new Map(snapshot.productTypes.map(t => [t.id, t]))
  const gmMap = new Map((snapshot.growingMaterials || []).map(g => [g.id, g]))

  return items.map(item => ({
    ...item,
    items: item.items.map(li => {
      // Growing materials: strain rendered in primary column (like blocks),
      // quantity x format in the wide cell. Pulls strain from gm.product
      // when available, falling back to the gm name.
      if (li.growingMaterialId) {
        const gm = li.growingMaterial || gmMap.get(li.growingMaterialId) || null
        const strain = gm?.product?.strain
          || productMap.get(gm?.product_id)?.strain
          || li.strain
          || gm?.name
          || 'Growing Material'
        return {
          ...li,
          growingMaterial: gm,
          strain,
          productTypeName: gm?.unit_label || ''
        }
      }
      const strain = li.strain || productMap.get(li.productId)?.strain || 'Unknown'
      let productTypeName = li.productTypeName || typeMap.get(li.productTypeId)?.name || 'Unknown'
      if (li.blockFormatId) {
        productTypeName = `Block (${li.blockFormat?.name || 'Unknown'})`
      }
      return { ...li, strain, productTypeName }
    })
  }))
}

/**
 * Fetch a chunk of older sales for infinite scroll.
 */
async function fetchOlderSalesLightweight(toDate, days) {
  const toDateObj = typeof toDate === 'string' ? parseDateString(toDate) : toDate
  const fromDate = new Date(toDateObj)
  fromDate.setDate(fromDate.getDate() - days)
  const fromDateStr = toLocalDateString(fromDate)
  const toDateStr = typeof toDate === 'string' ? toDate : toLocalDateString(toDate)

  const sales = await fetchSalesLightweight(fromDateStr, toDateStr)
  const items = (sales || []).map(transformLightweightToTimelineItem)
  return items.sort((a, b) => b.date.localeCompare(a.date))
}

/**
 * Fetch all sales (past + future confirmed) using lightweight query.
 * Extends forward by CONFIRMED_FUTURE_DAYS so manually-created future sales
 * are always visible, no matter how far ahead the user schedules them.
 */
async function fetchAllSalesLightweight(today, pastDays) {
  const todayObj = parseDateString(today)
  const fromDate = new Date(todayObj)
  fromDate.setDate(fromDate.getDate() - pastDays)
  const fromDateStr = toLocalDateString(fromDate)

  const toDate = new Date(todayObj)
  toDate.setDate(toDate.getDate() + CONFIRMED_FUTURE_DAYS)
  const toDateStr = toLocalDateString(toDate)

  const sales = await fetchSalesLightweight(fromDateStr, toDateStr)
  const items = (sales || []).map(transformLightweightToTimelineItem)
  return items.sort((a, b) => b.date.localeCompare(a.date))
}

/**
 * Insert a timeline item into a date-descending sorted array.
 * Removes any existing item with the same id before inserting.
 */
function insertSorted(prev, item) {
  const without = prev.filter(i => i.id !== item.id)
  // Binary-search would be faster, but n is small enough for a simple approach
  const idx = without.findIndex(i => i.date <= item.date)
  if (idx === -1) return [...without, item]
  return [...without.slice(0, idx), item, ...without.slice(idx)]
}

export function useTimelineData() {
  const { snapshot } = useDataCache()
  const snapshotRef = useRef(snapshot)

  // Keep snapshotRef in sync so loadMorePast reads latest without re-creating callback
  snapshotRef.current = snapshot

  // State for future items (unconfirmed)
  const [futureItems, setFutureItems] = useState([])
  const [loadingFuture, setLoadingFuture] = useState(false)
  const [futureDaysLoaded, setFutureDaysLoaded] = useState(INITIAL_FUTURE_DAYS)
  const [hasMoreFuture, setHasMoreFuture] = useState(true)

  // State for past items (confirmed)
  const [pastItems, setPastItems] = useState([])
  const [allPastItems, setAllPastItems] = useState([]) // Unfiltered past items
  const [loadingPast, setLoadingPast] = useState(false)
  const [pastDaysLoaded, setPastDaysLoaded] = useState(INITIAL_PAST_DAYS)
  const [hasMorePast, setHasMorePast] = useState(true)

  // Loading guard refs — synchronous check to prevent sentinel re-fires
  const loadingFutureRef = useRef(false)
  const loadingPastRef = useRef(false)
  const emptyFutureChunksRef = useRef(0)
  const emptyPastChunksRef = useRef(0)

  // Dedup refs — stable identity for callback dependency arrays
  const pastItemIdsRef = useRef(new Set())
  const futureItemIdsRef = useRef(new Set())

  // Signal that the first network fetch has completed (data is settled).
  // Prevents the timeline from scrolling to TODAY using stale session-cache
  // data that gets shifted when the background refresh prepends more groups.
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)

  // State for item interactions
  const [confirmingIds, setConfirmingIds] = useState(new Set())
  const [quantityEdits, setQuantityEdits] = useState({}) // { itemId: { productId: newQuantity } }
  const [searchTerm, setSearchTerm] = useState('')

  // State for price recalculation on pending→packed
  const [pendingPriceCheck, setPendingPriceCheck] = useState(null)

  /**
   * Get today's date in YYYY-MM-DD format
   */
  const getTodayString = useCallback(() => {
    return toLocalDateString(new Date())
  }, [])

  /**
   * Initial data load on mount — stale-while-revalidate from sessionStorage
   */
  useEffect(() => {
    const today = getTodayString()

    // 1. Try session cache first — instant render, no spinners
    const cached = loadTimelineFromSession()
    if (cached) {
      const enrichedFuture = enrichFromCache(cached.future, snapshot)
      const enrichedPast = enrichFromCache(cached.past, snapshot)
      futureItemIdsRef.current = new Set(enrichedFuture.map(i => i.id))
      pastItemIdsRef.current = new Set(enrichedPast.map(i => i.id))
      setFutureItems(enrichedFuture)
      setPastItems(enrichedPast)
      setAllPastItems(enrichedPast)
      console.log('[useTimelineData] Loaded from session cache, refreshing in background')
    }

    // 2. Background refresh (always runs — fresh data replaces stale)
    const refresh = async () => {
      if (!cached) {
        setLoadingPast(true)
      }

      try {
        const past = await fetchAllSalesLightweight(today, INITIAL_PAST_DAYS)
          .then(items => enrichFromCache(items, snapshotRef.current))

        pastItemIdsRef.current = new Set(past.map(i => i.id))
        setFutureItems([])
        setPastItems(past)
        setAllPastItems(past)
        saveTimelineToSession([], past)
      } catch (error) {
        console.error('[useTimelineData] Error loading timeline data:', error)
        if (!cached) {
          toast.error('Failed to load sales data', { description: error.message })
        }
      } finally {
        setLoadingFuture(false)
        setLoadingPast(false)
        setInitialLoadComplete(true)
      }
    }

    refresh()
  }, [getTodayString]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Re-enrich timeline items when product cache becomes available.
   * Covers two race conditions:
   *  1. Products load AFTER timeline fetch → snapshot changes trigger this
   *  2. Products load BEFORE timeline fetch → initialLoadComplete trigger this
   */
  useEffect(() => {
    if (!snapshot?.products || !snapshot?.productTypes) return

    const needsEnrichment = (items) =>
      items.some(item => item.items.some(li => !li.strain || li.strain === 'Unknown'))

    setAllPastItems(prev => {
      if (prev.length === 0 || !needsEnrichment(prev)) return prev
      return enrichFromCache(prev, snapshot)
    })

    setFutureItems(prev => {
      if (prev.length === 0 || !needsEnrichment(prev)) return prev
      return enrichFromCache(prev, snapshot)
    })
  }, [snapshot, initialLoadComplete])

  /**
   * Load more future items (7-day chunks, max 6 months)
   */
  const loadMoreFuture = useCallback(async () => {
    if (loadingFutureRef.current || !hasMoreFuture) return

    // Check if we've reached the max horizon
    if (futureDaysLoaded >= MAX_FUTURE_DAYS) {
      setHasMoreFuture(false)
      return
    }

    loadingFutureRef.current = true
    setLoadingFuture(true)

    try {
      const today = getTodayString()
      const fromDate = parseDateString(today)
      fromDate.setDate(fromDate.getDate() + futureDaysLoaded)
      const fromDateStr = toLocalDateString(fromDate)

      const newItems = await fetchTimelineFuture(fromDateStr, FUTURE_LOAD_CHUNK)

      // Dedupe using ref instead of state
      const genuinelyNew = newItems.filter(item => !futureItemIdsRef.current.has(item.id))

      if (genuinelyNew.length === 0) {
        emptyFutureChunksRef.current++
        if (emptyFutureChunksRef.current >= 2) {
          setHasMoreFuture(false)
        }
      } else {
        emptyFutureChunksRef.current = 0
        genuinelyNew.forEach(item => futureItemIdsRef.current.add(item.id))
        setFutureItems(prev => [...prev, ...genuinelyNew])
      }

      const newDaysLoaded = futureDaysLoaded + FUTURE_LOAD_CHUNK
      setFutureDaysLoaded(newDaysLoaded)

      if (newDaysLoaded >= MAX_FUTURE_DAYS) {
        setHasMoreFuture(false)
      }
    } catch (error) {
      console.error('[useTimelineData] Error loading more future items:', error)
      toast.error('Failed to load more upcoming orders', {
        description: error.message
      })
    } finally {
      loadingFutureRef.current = false
      setLoadingFuture(false)
    }
  }, [hasMoreFuture, futureDaysLoaded, getTodayString])

  /**
   * Load more past items (90-day chunks, unlimited)
   */
  const loadMorePast = useCallback(async () => {
    if (loadingPastRef.current || !hasMorePast) return

    loadingPastRef.current = true
    setLoadingPast(true)

    try {
      const today = getTodayString()
      const toDate = parseDateString(today)
      toDate.setDate(toDate.getDate() - pastDaysLoaded)
      const toDateStr = toLocalDateString(toDate)

      const newItems = enrichFromCache(
        await fetchOlderSalesLightweight(toDateStr, PAST_LOAD_CHUNK),
        snapshotRef.current
      )

      // Dedupe using ref instead of state
      const genuinelyNew = newItems.filter(item => !pastItemIdsRef.current.has(item.id))

      if (genuinelyNew.length === 0) {
        emptyPastChunksRef.current++
        if (emptyPastChunksRef.current >= 2) {
          setHasMorePast(false)
        }
      } else {
        emptyPastChunksRef.current = 0
        genuinelyNew.forEach(item => pastItemIdsRef.current.add(item.id))
        setAllPastItems(prev => [...prev, ...genuinelyNew])
      }

      setPastDaysLoaded(prev => prev + PAST_LOAD_CHUNK)
    } catch (error) {
      console.error('[useTimelineData] Error loading more past items:', error)
      toast.error('Failed to load more sales history', {
        description: error.message
      })
    } finally {
      loadingPastRef.current = false
      setLoadingPast(false)
    }
  }, [hasMorePast, pastDaysLoaded, getTodayString])

  /**
   * Apply search filter to past items
   */
  useEffect(() => {
    if (!searchTerm.trim()) {
      setPastItems(allPastItems)
      return
    }

    const term = searchTerm.toLowerCase()
    const filtered = allPastItems.filter(item => {
      // Search across customer name, branch name, consignment no
      const customerMatch = item.customer?.name?.toLowerCase().includes(term)
      const branchMatch = item.branch?.name?.toLowerCase().includes(term)
      const consignmentMatch = item.consignmentNo?.toLowerCase().includes(term)
      const invoiceMatch = item.invoiceNo?.toLowerCase().includes(term)

      return customerMatch || branchMatch || consignmentMatch || invoiceMatch
    })

    setPastItems(filtered)
  }, [searchTerm, allPastItems])

  /**
   * Debounced persist to sessionStorage after timeline mutations
   */
  useEffect(() => {
    if (futureItems.length === 0 && allPastItems.length === 0) return
    const timer = setTimeout(() => {
      saveTimelineToSession(futureItems, allPastItems)
    }, 1000)
    return () => clearTimeout(timer)
  }, [futureItems, allPastItems])

  /**
   * Real-time subscription on sales table — INSERT/UPDATE
   */
  useEffect(() => {
    const channel = supabase
      .channel('sales-timeline-updates')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sales' }, async (payload) => {
        const saleId = payload.new?.id
        const itemId = `sale-${saleId}`
        if (!saleId || pastItemIdsRef.current.has(itemId)) return

        try {
          const fullSale = await fetchSaleById(saleId)
          const item = transformToTimelineItem(fullSale, fullSale.recurring_order_id ? 'committed-recurring' : 'confirmed')

          pastItemIdsRef.current.add(item.id)
          setAllPastItems(prev => insertSorted(prev, item))
        } catch (err) {
          console.error('[useTimelineData] RT INSERT error:', err)
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'sales' }, async (payload) => {
        const saleId = payload.new?.id
        const itemId = `sale-${saleId}`
        if (!saleId || !pastItemIdsRef.current.has(itemId)) return

        try {
          const fullSale = await fetchSaleById(saleId)
          const item = transformToTimelineItem(fullSale, fullSale.recurring_order_id ? 'committed-recurring' : 'confirmed')
          const replace = (items) => insertSorted(items.filter(i => i.id !== item.id), item)
          setAllPastItems(replace)
          setPastItems(replace)
        } catch (err) {
          console.error('[useTimelineData] RT UPDATE error:', err)
        }
      })
      .subscribe()

    return () => { channel.unsubscribe() }
  }, [])

  /**
   * Search past items only (future always visible)
   */
  const searchPast = useCallback((term) => {
    setSearchTerm(term)
  }, [])

  /**
   * Refresh future items from scratch
   * Used after editing recurring orders to reflect changes
   */
  const refreshFutureItems = useCallback(async () => {
    const today = getTodayString()

    setLoadingFuture(true)
    try {
      const futureRaw = await fetchTimelineFuture(today, futureDaysLoaded)
      const future = enrichFromCache(futureRaw, snapshotRef.current)
      futureItemIdsRef.current = new Set(future.map(item => item.id))
      setFutureItems(future)
      // Update session cache so stale data doesn't reappear on navigation
      setAllPastItems(prev => {
        saveTimelineToSession(future, prev)
        return prev
      })
    } catch (error) {
      console.error('[useTimelineData] Error refreshing future items:', error)
      toast.error('Failed to refresh upcoming orders', {
        description: error.message
      })
    } finally {
      setLoadingFuture(false)
    }
  }, [getTodayString, futureDaysLoaded])

  /**
   * Update quantity for a specific product in an item
   *
   * @param {string} itemId - Timeline item ID
   * @param {string} productId - Product ID to update
   * @param {number} quantity - New quantity
   */
  const updateQuantity = useCallback((itemId, productId, quantity) => {
    setQuantityEdits(prev => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        [productId]: quantity
      }
    }))
  }, [])

  /**
   * Confirm an unconfirmed item (recurring instance or suggestion)
   * Creates a sale and updates local state optimistically
   *
   * @param {Object} item - Timeline item to confirm
   * @returns {Promise<Object>} Created sale
   */
  const confirmItem = useCallback(async (item) => {
    // Validate item type
    if (item.type !== 'unconfirmed-recurring' && item.type !== 'unconfirmed-suggestion') {
      toast.error('Cannot confirm this item', {
        description: 'Only unconfirmed recurring orders and suggestions can be confirmed'
      })
      return null
    }

    // Mark as confirming
    setConfirmingIds(prev => new Set([...prev, item.id]))

    try {
      let sale

      // Prepare quantity edits if any exist for this item
      const edits = quantityEdits[item.id] || {}
      const hasEdits = Object.keys(edits).length > 0

      if (item.type === 'unconfirmed-recurring') {
        // Confirm recurring order instance
        const modifications = {}

        if (hasEdits) {
          // Apply quantity edits to items
          modifications.items = item.items.map(lineItem => ({
            productId: lineItem.productId,
            productTypeId: lineItem.productTypeId,
            quantity: edits[lineItem.productId] ?? lineItem.quantity
          }))
        }

        sale = await confirmInstance(item.sourceId, modifications)
      } else if (item.type === 'unconfirmed-suggestion') {
        // For suggestions grouped by date+customer+branch, we need to confirm each suggestion separately
        // This is a simplified approach - the actual implementation may need to handle multiple suggestions
        const suggestionEdits = {}

        if (hasEdits && item.items.length > 0) {
          const firstItem = item.items[0]
          suggestionEdits.quantity = edits[firstItem.productId] ?? firstItem.quantity
        }

        // Note: confirmSuggestion works on individual suggestions, not grouped items
        // This hook assumes the TimelineItem for suggestions represents a single suggestion
        // If grouping is complex, this logic may need adjustment
        if (item.items.length > 0) {
          const firstItem = item.items[0]
          sale = await confirmSuggestion(firstItem.id, suggestionEdits)
        } else {
          throw new Error('Suggestion has no items to confirm')
        }
      }

      // Pre-register sale ID in refs to prevent the realtime subscription
      // from adding a duplicate during the fetchSaleById async gap
      const saleItemId = `sale-${sale.id}`
      futureItemIdsRef.current.add(saleItemId)
      pastItemIdsRef.current.add(saleItemId)

      // Fetch the full sale with items to get correct prices and totals
      let confirmedItem
      try {
        const fullSale = await fetchSaleById(sale.id)
        const type = fullSale.recurring_order_id ? 'committed-recurring' : 'confirmed'
        confirmedItem = transformToTimelineItem(fullSale, type)
      } catch (fetchErr) {
        console.warn('[useTimelineData] fetchSaleById failed, using sale data from confirm:', fetchErr)
        // Fallback: build a minimal confirmed item from the data we have
        confirmedItem = {
          id: saleItemId,
          type: 'committed-recurring',
          sourceId: sale.id,
          date: item.date,
          customer: item.customer,
          branch: item.branch,
          items: item.items,
          total: item.total,
          status: 'pending',
          consignmentNo: sale.consignment_no || null,
          invoiceNo: null,
          recurringOrderId: item.recurringOrderId || null
        }
      }

      // Add confirmed item to past items (sorted by date)
      pastItemIdsRef.current.add(confirmedItem.id)
      setFutureItems(prev => prev.filter(i => i.id !== item.id))
      setAllPastItems(prev => insertSorted(prev, confirmedItem))
      setPastItems(prev => {
        if (!searchTerm.trim()) return insertSorted(prev, confirmedItem)

        const term = searchTerm.toLowerCase()
        const matches = confirmedItem.customer?.name?.toLowerCase().includes(term) ||
                        confirmedItem.branch?.name?.toLowerCase().includes(term) ||
                        confirmedItem.consignmentNo?.toLowerCase().includes(term)

        return matches ? insertSorted(prev, confirmedItem) : prev
      })

      // Clear quantity edits for this item
      setQuantityEdits(prev => {
        const updated = { ...prev }
        delete updated[item.id]
        return updated
      })

      toast.success('Order confirmed', {
        description: `Created sale for ${item.customer.name}`
      })

      return sale
    } catch (error) {
      console.error('[useTimelineData] Error confirming item:', error)

      toast.error('Failed to confirm order', {
        description: error.message
      })

      // No state rollback needed since we use optimistic updates only on success
      return null
    } finally {
      // Remove from confirming set
      setConfirmingIds(prev => {
        const updated = new Set(prev)
        updated.delete(item.id)
        return updated
      })
    }
  }, [quantityEdits, searchTerm])

  /**
   * Dismiss an unconfirmed item (removes it from view without confirming)
   *
   * @param {Object} item - Timeline item to dismiss
   */
  const dismissItem = useCallback((item) => {
    // Validate item type
    if (item.type !== 'unconfirmed-recurring' && item.type !== 'unconfirmed-suggestion') {
      toast.error('Cannot dismiss this item', {
        description: 'Only unconfirmed items can be dismissed'
      })
      return
    }

    // Remove from future items
    setFutureItems(prev => prev.filter(i => i.id !== item.id))

    // Clear any quantity edits for this item
    setQuantityEdits(prev => {
      const updated = { ...prev }
      delete updated[item.id]
      return updated
    })

    toast.success('Item dismissed', {
      description: `Removed ${item.customer.name} suggestion`
    })
  }, [])

  /**
   * Update an existing sale in the timeline after editing
   *
   * @param {string} saleId - The ID of the sale to update
   */
  const updateSaleInTimeline = useCallback(async (saleId) => {
    try {
      const fullSale = await fetchSaleById(saleId)
      const timelineItem = transformToTimelineItem(fullSale, 'confirmed')

      const replaceItem = (items) =>
        items.map(i => i.id === timelineItem.id ? timelineItem : i)

      setAllPastItems(replaceItem)
      setPastItems(replaceItem)
    } catch (error) {
      console.error('[useTimelineData] Error updating sale in timeline:', error)
    }
  }, [])

  /**
   * Update status for a confirmed sale
   *
   * @param {string} itemId - Timeline item ID (format: "sale-{saleId}")
   * @param {string} newStatus - New status value
   */
  const updateStatus = useCallback(async (itemId, newStatus) => {
    // Extract sale ID from timeline item ID
    const saleId = itemId.replace('sale-', '')

    // Find the item across all timeline sections (today's sales live in futureItems)
    const item = pastItems.find(i => i.id === itemId)
      || allPastItems.find(i => i.id === itemId)
      || futureItems.find(i => i.id === itemId)
    if (!item) {
      toast.error('Could not find item to update')
      return
    }

    // Guard: prevent status regression on invoiced/paid sales
    if ((item.status === 'invoiced' || item.status === 'paid') && newStatus !== 'paid') {
      toast.error('Cannot change status: this sale is linked to an invoice')
      return
    }

    const isInFuture = !pastItemIdsRef.current.has(itemId)
    const previousStatus = item.status
    const previousConsignmentNo = item.consignmentNo

    // Intercept pending→packed/delivered/collected/sent: recalculate prices before transitioning
    if (previousStatus === 'pending' && ['packed', 'delivered', 'collected', 'sent'].includes(newStatus)) {
      setPendingPriceCheck({ itemId, saleId, newStatus, previousStatus, loading: true, priceComparison: null })

      try {
        const comparison = await recalculateSaleItemPrices(saleId)

        if (!comparison.hasChanges) {
          // No price changes - proceed with normal status update
          setPendingPriceCheck(null)
          // Fall through to normal update below
        } else {
          // Prices changed - show confirmation dialog (don't update status yet)
          setPendingPriceCheck({ itemId, saleId, newStatus, previousStatus, loading: false, priceComparison: comparison })
          return
        }
      } catch (error) {
        console.error('[useTimelineData] Error recalculating prices:', error)
        setPendingPriceCheck(null)
        toast.error('Failed to check prices', { description: error.message })
        return
      }
    }

    // Optimistic update
    const updateItemStatus = (items) =>
      items.map(i => i.id === itemId ? { ...i, status: newStatus } : i)

    if (isInFuture) {
      setFutureItems(updateItemStatus)
    } else {
      setPastItems(updateItemStatus)
      setAllPastItems(updateItemStatus)
    }

    try {
      const updatedSale = await updateSaleStatus(saleId, newStatus)

      // Merge server-assigned fields (consignment_no + tray numbers)
      const mergeServerData = (items) =>
        items.map(i => {
          if (i.id !== itemId) return i
          const updates = { ...i }
          if (updatedSale?.consignment_no) {
            updates.consignmentNo = updatedSale.consignment_no
          }
          if (updatedSale?.updatedItems) {
            updates.items = i.items.map(lineItem => {
              const match = updatedSale.updatedItems.find(ui => ui.id === lineItem.id)
              return match ? { ...lineItem, itemNumber: match.item_number } : lineItem
            })
          }
          return updates
        })

      if (isInFuture) {
        setFutureItems(mergeServerData)
      } else {
        setPastItems(mergeServerData)
        setAllPastItems(mergeServerData)
      }

      toast.success('Status updated', {
        description: `${item.customer.name} → ${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)}`
      })
    } catch (error) {
      console.error('[useTimelineData] Error updating status:', error)

      // Rollback on failure (both status and consignment_no)
      const rollback = (items) =>
        items.map(i => i.id === itemId
          ? { ...i, status: previousStatus, consignmentNo: previousConsignmentNo }
          : i
        )

      if (isInFuture) {
        setFutureItems(rollback)
      } else {
        setPastItems(rollback)
        setAllPastItems(rollback)
      }

      toast.error('Failed to update status', {
        description: error.message
      })
    }
  }, [pastItems, allPastItems, futureItems])

  /**
   * Confirm price update after recalculation dialog
   * @param {boolean} applyNewPrices - true to update prices, false to keep old prices
   */
  const confirmPriceUpdate = useCallback(async (applyNewPrices) => {
    if (!pendingPriceCheck) return

    const { itemId, saleId, newStatus, previousStatus, priceComparison } = pendingPriceCheck

    // Find the item across all timeline sections for toast message
    const item = pastItems.find(i => i.id === itemId)
      || allPastItems.find(i => i.id === itemId)
      || futureItems.find(i => i.id === itemId)
    const isInFuture = !pastItemIdsRef.current.has(itemId)

    // Optimistic update
    const updateItemStatus = (items) =>
      items.map(i => i.id === itemId ? { ...i, status: newStatus } : i)

    if (isInFuture) {
      setFutureItems(updateItemStatus)
    } else {
      setPastItems(updateItemStatus)
      setAllPastItems(updateItemStatus)
    }
    setPendingPriceCheck(null)

    try {
      let updatedSale
      if (applyNewPrices && priceComparison) {
        updatedSale = await applySaleItemPriceUpdates(priceComparison, newStatus)
        toast.success('Prices updated & packed', {
          description: `${item?.customer?.name || priceComparison.customerName} — prices updated to current catalogue`
        })
      } else {
        updatedSale = await updateSaleStatus(saleId, newStatus)
        toast.success('Status updated', {
          description: `${item?.customer?.name || 'Sale'} → ${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)} (old prices kept)`
        })
      }

      // Update consignment number and item prices in timeline state
      const mergeServerData = (items) =>
        items.map(i => {
          if (i.id !== itemId) return i
          const updates = { ...i }
          if (updatedSale?.consignment_no) {
            updates.consignmentNo = updatedSale.consignment_no
          }
          // Sync item prices after price recalculation
          if (applyNewPrices && priceComparison?.items) {
            updates.items = i.items.map(lineItem => {
              const changed = priceComparison.items.find(ci => ci.saleItemId === lineItem.id)
              if (changed) {
                return { ...lineItem, price: changed.newPrice, subtotal: changed.newSubtotal }
              }
              return lineItem
            })
            updates.total = updates.items.reduce((sum, li) => sum + li.subtotal, 0)
          }
          // Merge tray numbers from packing
          if (updatedSale?.updatedItems) {
            updates.items = (updates.items || i.items).map(lineItem => {
              const match = updatedSale.updatedItems.find(ui => ui.id === lineItem.id)
              return match ? { ...lineItem, itemNumber: match.item_number } : lineItem
            })
          }
          return updates
        })

      if (isInFuture) {
        setFutureItems(mergeServerData)
      } else {
        setPastItems(mergeServerData)
        setAllPastItems(mergeServerData)
      }
    } catch (error) {
      console.error('[useTimelineData] Error confirming price update:', error)

      // Rollback on failure
      const rollbackStatus = (items) =>
        items.map(i => i.id === itemId ? { ...i, status: previousStatus } : i)

      if (isInFuture) {
        setFutureItems(rollbackStatus)
      } else {
        setPastItems(rollbackStatus)
        setAllPastItems(rollbackStatus)
      }

      toast.error('Failed to update', { description: error.message })
    }
  }, [pendingPriceCheck, pastItems, allPastItems, futureItems])

  /**
   * Cancel price update - reverts dropdown to previous status
   */
  const cancelPriceUpdate = useCallback(() => {
    setPendingPriceCheck(null)
  }, [])

  /**
   * Add or replace a sale in the timeline (used after Create/Edit Sale modal)
   *
   * @param {string} saleId - The sale ID to fetch and insert
   */
  const addSaleToTimeline = useCallback(async (saleId) => {
    try {
      const fullSale = await fetchSaleById(saleId)
      const type = fullSale.recurring_order_id ? 'committed-recurring' : 'confirmed'
      const timelineItem = transformToTimelineItem(fullSale, type)
      const itemId = timelineItem.id

      // Insert or replace in allPastItems (sorted by date)
      setAllPastItems(prev => insertSorted(prev, timelineItem))

      // Insert or replace in pastItems (respecting search filter)
      setPastItems(prev => {
        if (!searchTerm.trim()) return insertSorted(prev, timelineItem)

        const term = searchTerm.toLowerCase()
        const matches = timelineItem.customer?.name?.toLowerCase().includes(term) ||
                        timelineItem.branch?.name?.toLowerCase().includes(term) ||
                        timelineItem.consignmentNo?.toLowerCase().includes(term)

        return matches ? insertSorted(prev, timelineItem) : prev.filter(i => i.id !== timelineItem.id)
      })
    } catch (error) {
      console.error('[useTimelineData] Error adding sale to timeline:', error)
    }
  }, [searchTerm])

  /**
   * Optimistically update a specific sale item's quantity in the timeline.
   * Used by PackingModal to reflect weight changes immediately.
   *
   * @param {string} saleId - The sale ID
   * @param {string} saleItemId - The sale item ID to update
   * @param {number} newQuantity - New quantity value
   */
  const updateSaleItemInTimeline = useCallback((saleId, saleItemId, newQuantity) => {
    const itemId = `sale-${saleId}`

    const updater = (items) => items.map(item => {
      if (item.id !== itemId) return item
      const updatedItems = item.items.map(li =>
        li.id === saleItemId
          ? { ...li, quantity: newQuantity, subtotal: newQuantity * li.price }
          : li
      )
      return {
        ...item,
        items: updatedItems,
        total: updatedItems.reduce((sum, li) => sum + li.subtotal, 0)
      }
    })

    setPastItems(updater)
    setAllPastItems(updater)
    setFutureItems(updater)
  }, [])

  /**
   * Remove a sale from the timeline (used after deleting a sale)
   *
   * @param {string} saleId - The sale ID to remove
   */
  const removeSaleFromTimeline = useCallback((saleId) => {
    const itemId = `sale-${saleId}`
    setPastItems(prev => prev.filter(i => i.id !== itemId))
    setAllPastItems(prev => prev.filter(i => i.id !== itemId))
  }, [])

  // --- Optimistic mutation helpers ---

  /**
   * Add an optimistic timeline item (temp ID, instant UI)
   * @param {Object} payload - Optimistic sale payload from CreateSaleModal
   * @returns {string} Temporary timeline item ID
   */
  const addSaleOptimistically = useCallback((payload) => {
    const tempId = `sale-temp-${Date.now()}`
    const item = {
      id: tempId,
      type: 'confirmed',
      sourceId: null,
      date: typeof payload.date === 'string'
        ? payload.date
        : payload.date instanceof Date
          ? toLocalDateString(payload.date)
          : toLocalDateString(new Date()),
      customer: { id: payload.customer_id, name: payload.customerName },
      branch: { id: payload.branch_id, name: payload.branchName },
      items: (payload.items || []).map(i => mapOptimisticItem(i)),
      total: payload.total || 0,
      status: 'pending',
      consignmentNo: null, // DB-generated
      invoiceNo: null,
      recurringOrderId: null,
      _optimistic: true
    }

    pastItemIdsRef.current.add(tempId)
    setAllPastItems(prev => insertSorted(prev, item))
    setPastItems(prev => insertSorted(prev, item))
    return tempId
  }, [getTodayString])

  /**
   * Update an existing sale optimistically (for edits)
   * @returns {Object} Previous item for rollback
   */
  const updateSaleOptimistically = useCallback((saleId, payload) => {
    const itemId = `sale-${saleId}`
    let previousItem = null

    const updater = (items) => {
      const existing = items.find(i => i.id === itemId)
      if (!existing) return items
      previousItem = existing
      const updatedItem = {
        ...existing,
        date: typeof payload.date === 'string'
          ? payload.date
          : payload.date instanceof Date
            ? toLocalDateString(payload.date)
            : existing.date,
        customer: { id: payload.customer_id, name: payload.customerName },
        branch: { id: payload.branch_id, name: payload.branchName },
        items: (payload.items || []).map(si => mapOptimisticItem(si)),
        total: payload.total || 0,
        _optimistic: true
      }
      return insertSorted(items.filter(i => i.id !== itemId), updatedItem)
    }

    // Today's sales live in futureItems, so update all three arrays
    setAllPastItems(updater)
    setPastItems(updater)
    setFutureItems(updater)
    return previousItem
  }, [])

  /**
   * Persist sale in the background after modal close.
   * On success: replace optimistic item with real data.
   * On failure: rollback and show error toast.
   */
  const persistSaleInBackground = useCallback(async (payload) => {
    const { _bgPayload, _tempId, _previousItem, _isEdit } = payload

    try {
      let savedSale

      let merged = false

      if (_isEdit) {
        // --- EDIT path ---
        savedSale = await updateSale(_bgPayload.saleId, _bgPayload.saleData)
        await deleteSaleItemsBySaleId(_bgPayload.saleId)
      } else {
        // --- CREATE path: check for existing mergeable sale ---
        const existingSale = await findExistingSale(
          _bgPayload.saleData.customer_id,
          _bgPayload.saleData.branch_id || null,
          _bgPayload.saleData.date
        )

        if (existingSale) {
          savedSale = existingSale
          merged = true
        } else {
          savedSale = await createSale(_bgPayload.saleData)
        }
      }

      // Create sale items
      const saleItemsData = _bgPayload.saleItemsData.map(item => ({
        ...item,
        sale_id: savedSale.id
      }))
      await createSaleItems(saleItemsData)

      // Optional: create recurring order (new sales only, not merged)
      if (!_isEdit && !merged && _bgPayload.recurring) {
        try {
          const ro = await createRecurringOrder(
            _bgPayload.recurring.orderConfig,
            _bgPayload.recurring.items
          )
          await generateInstances(ro.id, 6)
        } catch (recurringError) {
          console.error('[persistSaleInBackground] Recurring order error:', recurringError)
          toast.warning('Sale saved, but recurring order failed', {
            description: 'Please set up the recurring order from the customer page.'
          })
        }
      }

      // Fetch full sale for real data, replace optimistic item
      const fullSale = await fetchSaleById(savedSale.id)
      const realItem = transformToTimelineItem(fullSale, fullSale.recurring_order_id ? 'committed-recurring' : 'confirmed')

      if (_isEdit) {
        const editItemId = `sale-${_bgPayload.saleId}`
        const replace = (items) => insertSorted(items.filter(i => i.id !== editItemId), realItem)
        setAllPastItems(replace)
        setPastItems(replace)
        setFutureItems(replace)
      } else if (merged) {
        // Merged: remove temp optimistic item, update the existing timeline entry
        const existingItemId = `sale-${savedSale.id}`
        pastItemIdsRef.current.delete(_tempId)
        setAllPastItems(prev => prev.filter(i => i.id !== _tempId).map(i => i.id === existingItemId ? realItem : i))
        setPastItems(prev => prev.filter(i => i.id !== _tempId).map(i => i.id === existingItemId ? realItem : i))
      } else {
        // Replace temp item with real item
        const replacer = (items) => items.map(i => i.id === _tempId ? realItem : i)
        pastItemIdsRef.current.delete(_tempId)
        pastItemIdsRef.current.add(realItem.id)
        setAllPastItems(replacer)
        setPastItems(replacer)
      }

      if (merged) {
        toast.success('Items added to existing order', {
          description: `${payload.customerName} — ${realItem.consignmentNo || savedSale.id.slice(0, 8)}`
        })
      } else {
        toast.success(_isEdit ? 'Sale updated' : 'Sale created', {
          description: `${payload.customerName} — ${realItem.consignmentNo || 'saved'}`
        })
      }
    } catch (error) {
      console.error('[persistSaleInBackground] Error:', error)

      // Rollback
      if (_isEdit && _previousItem) {
        const editItemId = `sale-${_bgPayload.saleId}`
        const rollback = (items) => items.map(i => i.id === editItemId ? _previousItem : i)
        setAllPastItems(rollback)
        setPastItems(rollback)
        setFutureItems(rollback)
      } else if (_tempId) {
        pastItemIdsRef.current.delete(_tempId)
        setAllPastItems(prev => prev.filter(i => i.id !== _tempId))
        setPastItems(prev => prev.filter(i => i.id !== _tempId))
      }

      toast.error('Failed to save sale', {
        description: error.message,
        action: {
          label: 'Retry',
          onClick: () => persistSaleInBackground(payload)
        }
      })
    }
  }, [])

  return {
    futureItems,
    pastItems,
    loadingFuture,
    loadingPast,
    hasMoreFuture,
    hasMorePast,
    loadMoreFuture,
    loadMorePast,
    confirmItem,
    dismissItem,
    updateQuantity,
    updateStatus,
    addSaleToTimeline,
    updateSaleInTimeline,
    addSaleOptimistically,
    updateSaleOptimistically,
    persistSaleInBackground,
    confirmingIds,
    searchPast,
    refreshFutureItems,
    pendingPriceCheck,
    confirmPriceUpdate,
    cancelPriceUpdate,
    addSaleToTimeline,
    removeSaleFromTimeline,
    updateSaleItemInTimeline,
    initialLoadComplete
  }
}
